import {
  BroomIcon,
  CalendarStarIcon,
  ClockCountdownIcon,
  DotsThreeIcon,
  IslandIcon,
  type Icon as PhosphorIcon,
  SignInIcon,
  SignOutIcon,
  TrashIcon,
  WrenchIcon,
} from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import { z } from 'zod'

import { TimeBlock, TimeBlockReason } from '@/api/reservations/get-time-blocks'
import { Badge, BadgeColorKeys } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SelectOption } from '@/components/ui/select'
import { TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
// Note: startTime/endTime are "minutes since midnight" in building-local time, not epoch
// timestamps - formatMinutes formats them directly, no timezone conversion needed.
import { formatDateOnly, formatMinutes, formatTimeOnly } from '@/utils/date'

export const TIME_BLOCKS_TABLE_ID = 'reservations-time-blocks-table'

// Note: a null startTime/endTime means the block covers the full day (this is how
// multiday-booking time blocks were - and older ones still are - created; see
// ReservationsAddTimeBlockModal's onSubmit) - default to midnight/end-of-day for display
// instead of omitting the time, per EL-12414's AC ("shows date + time for both hourly and
// multiday time blocks").
const START_OF_DAY_MINUTES = 0
const END_OF_DAY_MINUTES = 23 * 60 + 59

// Note: kept static/untranslated - still consumed by the legacy (not-yet-migrated)
// ReservationsAddTimeBlockModal's create-flow Reason dropdown (TimeBlockSummary.jsx via
// TIME_BLOCK_REASON_CREATE_OPTIONS below). See getReasonLabelMap for the translated
// equivalent used by this feature's own table/filter.
// TODO: drop this map (and TIME_BLOCK_REASON_CREATE_OPTIONS) once that legacy modal is
// migrated to i18n - at that point it can consume getReasonLabelMap directly.
export const REASON_LABEL_MAP: Record<TimeBlockReason, string> = {
  'Building event': 'Building event',
  Cleaning: 'Cleaning',
  General: 'General',
  Holiday: 'Holiday',
  Maintenance: 'Maintenance',
  'Move in': 'Move in',
  'Move out': 'Move out',
  Other: 'Other',
}

// Note: translated equivalent of REASON_LABEL_MAP above, for this feature's own
// (migrated) call sites - see the TODO on REASON_LABEL_MAP for why both exist.
export const getReasonLabelMap = (
  t: TFunction<['reservations']>,
): Record<TimeBlockReason, string> => ({
  'Building event': t('reservations:timeBlocks.reasons.buildingEvent'),
  Cleaning: t('reservations:timeBlocks.reasons.cleaning'),
  General: t('reservations:timeBlocks.reasons.general'),
  Holiday: t('reservations:timeBlocks.reasons.holiday'),
  Maintenance: t('reservations:timeBlocks.reasons.maintenance'),
  'Move in': t('reservations:timeBlocks.reasons.moveIn'),
  'Move out': t('reservations:timeBlocks.reasons.moveOut'),
  Other: t('reservations:timeBlocks.reasons.other'),
})

export const REASON_COLOR_MAP: Record<TimeBlockReason, BadgeColorKeys> = {
  'Building event': 'purple',
  Cleaning: 'blue',
  General: 'neutral',
  Holiday: 'turquoise',
  Maintenance: 'orange',
  'Move in': 'green',
  'Move out': 'red',
  Other: 'neutral',
}

export const REASON_ICON_MAP: Record<TimeBlockReason, PhosphorIcon> = {
  'Building event': CalendarStarIcon,
  Cleaning: BroomIcon,
  General: ClockCountdownIcon,
  Holiday: IslandIcon,
  Maintenance: WrenchIcon,
  'Move in': SignInIcon,
  'Move out': SignOutIcon,
  Other: DotsThreeIcon,
}

export const ALL_REASON_VALUES: TimeBlockReason[] = Object.keys(
  REASON_LABEL_MAP,
) as TimeBlockReason[]

export const getReasonOptions = (
  t: TFunction<['reservations']>,
): SelectOption<TimeBlockReason>[] => {
  const labelMap = getReasonLabelMap(t)
  return ALL_REASON_VALUES.map((value) => ({ label: labelMap[value], value }))
}

// Note: display order for the Add Time Block create flow's Reason dropdown, per ticket spec -
// intentionally not alphabetical like getReasonOptions (used by the table's filter dropdown).
const TIME_BLOCK_REASON_CREATE_ORDER: TimeBlockReason[] = [
  'Move in',
  'Move out',
  'Cleaning',
  'Building event',
  'Maintenance',
  'Holiday',
  'General',
  'Other',
]

export const TIME_BLOCK_REASON_CREATE_OPTIONS: SelectOption<TimeBlockReason>[] =
  TIME_BLOCK_REASON_CREATE_ORDER.map((value) => ({
    badgeColor: REASON_COLOR_MAP[value],
    badgeVariant: 'light',
    icon: REASON_ICON_MAP[value],
    label: REASON_LABEL_MAP[value],
    value,
  }))

export const timeBlocksTableFiltersSchema = z.object({
  // Note: `allDates` = the date range was cleared (no date filter) - see the Reservations tab.
  allDates: z.stringbool().optional(),
  // Note: raw checkbox-tree selection ("a-<amenityId>" / "l-<locationId>") - see
  // splitAmenitySpaceSelection. One filter key for one filter control.
  amenitiesAndSpaces: z.array(z.string()).optional(),
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  reason: z.array(z.string()).optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  toDate: z.coerce.number().optional(),
})

export type TimeBlocksTableFilters = z.infer<typeof timeBlocksTableFiltersSchema>

export const timeBlocksTableInitialFilters: TimeBlocksTableFilters = {
  allDates: false,
  amenitiesAndSpaces: [],
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  reason: [],
  skip: 0,
  sortBy: 'date',
  sortOrder: 'desc',
  toDate: undefined,
}

interface TimeBlocksColumnHandlers {
  onDelete: (row: TimeBlock) => void
}

export const getTimeBlocksTableColumns = (
  t: TFunction<['reservations']>,
  timezone: string,
  { onDelete }: TimeBlocksColumnHandlers,
): TableColumn<TimeBlock>[] => {
  const reasonLabelMap = getReasonLabelMap(t)

  return [
    {
      id: 'reason',
      render: (row) => {
        const reason = row.reason ?? 'General'
        return (
          <Badge
            color={REASON_COLOR_MAP[reason]}
            icon={REASON_ICON_MAP[reason]}
            size="small"
            variant="light"
          >
            {reason === 'Other' ? row.reasonOther || reasonLabelMap.Other : reasonLabelMap[reason]}
          </Badge>
        )
      },
      title: t('reservations:timeBlocks.columns.reason'),
      width: '13rem',
    },
    {
      breakLines: 2,
      id: 'description',
      render: (row) => row.description || EMPTY_VALUE,
      title: t('reservations:timeBlocks.columns.description'),
      width: '18rem',
    },
    {
      id: 'amenity',
      render: (row) => (
        <div>
          <Typography color="black800" size="text-small" weight="medium">
            {row.amenityId?.name ?? EMPTY_VALUE}
          </Typography>
          {row.locationId && (
            <Typography color="black500" size="text-small">
              {row.locationId.name}
            </Typography>
          )}
        </div>
      ),
      title: t('reservations:timeBlocks.columns.amenity'),
      width: '18rem',
    },
    {
      id: 'start',
      render: (row) =>
        row.date ? (
          <div>
            <Typography color="black800" size="text-small">
              {formatDateOnly(toZonedTime(row.date, timezone))}
            </Typography>
            <Typography color="black800" size="text-small">
              {formatMinutes(row.startTime ?? START_OF_DAY_MINUTES, { padHours: false })}
            </Typography>
          </div>
        ) : (
          EMPTY_VALUE
        ),
      sortAscLabel: t('reservations:timeBlocks.sort.oldestFirst'),
      sortDescLabel: t('reservations:timeBlocks.sort.newestFirst'),
      sortKey: 'date',
      title: t('reservations:timeBlocks.columns.start'),
      width: '11rem',
    },
    {
      id: 'end',
      render: (row) => {
        const endDate = row.endDate ?? row.date
        return endDate ? (
          <div>
            <Typography color="black800" size="text-small">
              {formatDateOnly(toZonedTime(endDate, timezone))}
            </Typography>
            <Typography color="black800" size="text-small">
              {formatMinutes(row.endTime ?? END_OF_DAY_MINUTES, { padHours: false })}
            </Typography>
          </div>
        ) : (
          EMPTY_VALUE
        )
      },
      sortAscLabel: t('reservations:timeBlocks.sort.oldestFirst'),
      sortDescLabel: t('reservations:timeBlocks.sort.newestFirst'),
      sortKey: 'endDate',
      title: t('reservations:timeBlocks.columns.end'),
      width: '11rem',
    },
    {
      id: 'createdBy',
      render: (row) =>
        row.createdBy ? (
          <div>
            <Typography breakLines={2} color="black800" size="text-small" weight="medium">
              {row.createdBy.firstName} {row.createdBy.lastName}
            </Typography>
            <Typography color="black500" size="text-small">
              {formatDateOnly(toZonedTime(row.createdAt, timezone))},{' '}
              {formatTimeOnly(toZonedTime(row.createdAt, timezone))}
            </Typography>
          </div>
        ) : (
          EMPTY_VALUE
        ),
      title: t('reservations:timeBlocks.columns.createdBy'),
      width: '14rem',
    },
    {
      id: 'notesCount',
      render: (row) => row.notes.length,
      title: t('reservations:timeBlocks.columns.notesCount'),
      width: '8rem',
    },
    {
      excludeFromVisibilityControl: true,
      id: 'delete',
      render: (row) => (
        <Button
          onlyBox
          use="secondary"
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onDelete(row)
          }}
        >
          <Icon icon={TrashIcon} color="red500" />
        </Button>
      ),
      title: t('reservations:timeBlocks.columns.actions'),
      width: '5rem',
    },
  ]
}
